import VueRouter from "vue-router";

import { PushNotificationProvider, TrustedDeviceSecret } from "@/models";
import appInsights from "@/utils/appInsights";
import { Auth } from "@/utils/auth";
import env from "@/utils/env";
import { EventBus, EventNames, StartRefreshInterval, StopRefreshInterval } from "@/utils/eventBus";
import * as gtm from "@/utils/gtm";
import {
  BuildInfo,
  CheckBiometricsResponse,
  CheckBiometricsStatus,
  DecryptBiometricSecretResponse,
  DecryptBiometricSecretStatus,
  DeviceType,
  GetTrustedDeviceSecretResponse,
  NativeApp,
  NativeAppInfo,
  NativeAppPage,
  NativeAppType,
  PatientTab,
  SetupBiometricsResponse,
  SetupBiometricsStatus,
  VueAppInitialized,
  VueAppLoggedIn,
  VueAppNavigatedToPatient
} from "./";

// Let Typescript know that we'll be putting the NativeApp on window
declare global {
  interface Window {
    nativeApp?: BaseNativeApp;
  }
}

export type NativeFunction<T> = (info: NonNullable<T>) => void;

/**
 * These methods represent the communication from the web to the native app.
 *
 * Note: All of these methods require a single parameter and must return void
 */
export interface WebToNativeMethods {
  initialized: NativeFunction<VueAppInitialized>;
  loggedIn: NativeFunction<VueAppLoggedIn>;
  loggedOut: NativeFunction<{}>;
  navigateTo: NativeFunction<{ page: NativeAppPage }>;
  navigatedToPatient: NativeFunction<VueAppNavigatedToPatient>;
  saveTrustedDeviceSecret: NativeFunction<{ email: string; secret: string }>;
  getTrustedDeviceSecret: NativeFunction<{ email: string }>;
  checkBiometrics: NativeFunction<{}>;
  setupBiometrics: NativeFunction<{ email: string; secret: string }>;
  decryptBiometricSecret: NativeFunction<{}>;
  removeBiometricSecret: NativeFunction<{}>;
}

/**
 * These methods represent the communication from the native app to the web.
 *
 * These are not intended to be called by the website and should not be exposed on the NativeApp interface.
 */
export interface NativeToWebMethods {
  setDeviceInfo(info: NativeAppInfo): void;
  setDeviceToken(token: string): void;
  navigateToPatient(patientId: string): void;
  navigateToError(): void;
  sendGetTrustedDeviceSecretResponse(response: GetTrustedDeviceSecretResponse): void;
  sendCheckBiometricsResponse(response: CheckBiometricsResponse): void;
  sendSetupBiometricsResponse(response: SetupBiometricsResponse): void;
  sendDecryptBiometricSecretResponse(response: DecryptBiometricSecretResponse): void;
  appResumed(immediateRefresh: boolean): void;
  appPaused(): void;
}

/**
 * These are the methods from WebToNativeMethods that represent callbacks (where the native app is expected to send a
 * response). The type of each property represents the return type that the native app will send back.
 */
export interface WebToNativeCallbacks {
  getTrustedDeviceSecret: GetTrustedDeviceSecretResponse;
  checkBiometrics: CheckBiometricsResponse;
  setupBiometrics: SetupBiometricsResponse;
  decryptBiometricSecret: DecryptBiometricSecretResponse;
}

/**
 * This is a helper type for extracting the first (and only) parameter from a method in the WebToNativeMethods
 * interface
 */
export type WebToNativeMethodParameter<TMethod extends keyof WebToNativeMethods> = Parameters<
  WebToNativeMethods[TMethod]
>[0];

/**
 * Defines a function which takes 2 parameters:
 *  1) The name of a method to call on the native app (from the WebToNativeMethods interface)
 *  2) The parameter to send to the native app's method (from the WebToNativeMethods interface)
 */
export type CallNative = <TMethod extends keyof WebToNativeMethods>(
  method: TMethod,
  param: WebToNativeMethodParameter<TMethod>
) => void;

export type NativePromises = {
  [name in keyof WebToNativeCallbacks]?: { resolve: (result: unknown) => void; reject: () => void };
};

export const IsUsingBiometricsStorageKey = "Messenger_NativeApp_IsUsingBiometrics";

export abstract class BaseNativeApp implements NativeApp, NativeToWebMethods {
  abstract readonly isMobile: boolean;
  abstract readonly type: NativeAppType;
  abstract readonly pushNotificationProvider: PushNotificationProvider;

  abstract readonly callNative: CallNative;

  buildInfo: BuildInfo;
  deviceMake?: string;
  deviceModel?: string;
  abstract deviceOS?: string;

  hasBiometricsHardware = false;
  isUsingBiometrics = false;

  private nativePages: { [page in NativeAppPage]?: boolean } = {};

  private readonly nativePromises: NativePromises = {};

  get deviceType() {
    return this.isMobile ? DeviceType.Mobile : DeviceType.Web;
  }

  constructor(private router: VueRouter, protected auth: Auth) {
    // Inititialize the build info to the web's build details and then the native app will override this during
    // initialization
    this.buildInfo = this.createBuildInfo(env.buildNumber, env.buildDate);

    this.loadIsUsingBiometrics();

    // Subscribe to events for when the user has logged in or logged out
    // FYI use an arrow function to ensure that "this" means the BaseNativeApp class instance
    EventBus.$on(EventNames.LoggedIn, () => this.loggedIn());
    EventBus.$on(EventNames.LoggedOut, () => this.loggedOut());
  }

  register(): void {
    window.nativeApp = this;

    this.sendInitialized();
  }

  createBuildInfo(buildNumber: string, buildDate: string) {
    // Regex explanation:
    // 1st group: Get the major.minor.maintenance version with the optional pre-release version (e.g. 2.4.4-rc1)
    // 2nd group (optional): Get the pre-release version (e.g. -rc1)
    // 3rd group (optional): Get the Git commit metadata used as the revision with the leading "+" (e.g. +12-gc123123)
    // 4th group (optional): Get the Git commit metadata used as the revision without the leading "+" (e.g. 12-gc123123)
    const regex = /^(\d+\.\d+\.\d+(-[^+]+)?)(\+(.*))?$/;

    const matches = buildNumber?.match(regex) || [];

    return {
      full: buildNumber,
      version: matches[1] || "",
      revision: matches[4] || "",
      date: buildDate
    };
  }

  private loadIsUsingBiometrics() {
    this.isUsingBiometrics = localStorage.getItem(IsUsingBiometricsStorageKey) === "true";
  }

  private setIsUsingBiometrics(enabled: boolean) {
    this.isUsingBiometrics = enabled;
    localStorage.setItem(IsUsingBiometricsStorageKey, enabled ? "true" : "");
  }

  // ----------------- Vue.js to Native -----------------
  private sendInitialized(): void {
    this.callNative("initialized", {
      jwt: this.auth.isLoggedIn() ? this.auth.token : undefined,
      config: env.nativeApp
    });
  }

  private loggedIn(): void {
    if (!this.auth.user) {
      console.warn("Not sending loggedIn event to native app, because user is not logged in");
      return;
    }

    this.callNative("loggedIn", {
      jwt: this.auth.token,
      username: this.auth.user.email
    });
  }

  private loggedOut(): void {
    this.callNative("loggedOut", {});
  }

  canNavigateTo(page: NativeAppPage): boolean {
    return !!this.nativePages[page];
  }

  navigateTo(page: NativeAppPage): void {
    this.callNative("navigateTo", { page });
  }

  navigatedToPatient(patientId: number, tab?: PatientTab): void {
    this.callNative("navigatedToPatient", { patientId, tab });
  }

  saveTrustedDeviceSecret(secret: TrustedDeviceSecret): void {
    if (!this.auth.user) {
      console.warn("Could not save trusted device secret because user is not logged in");
      return;
    }

    // Put the email is in lower case so that it always matches, regardless of how the user typed it
    const email = this.auth.user.email.toLowerCase();
    this.callNative("saveTrustedDeviceSecret", { email, secret });
  }

  async getTrustedDeviceSecret(email: string): Promise<TrustedDeviceSecret | undefined> {
    try {
      const response = await this.callNativeCodeAndWait("getTrustedDeviceSecret", {
        // Put the email is in lower case so that it always matches, regardless of how the user typed it
        email: email.toLowerCase()
      });
      return response.secret;
    } catch (error) {
      // This is only called if this method was triggered again without the promise being resolved
      return undefined;
    }
  }

  async checkBiometrics(): Promise<CheckBiometricsResponse> {
    try {
      return await this.callNativeCodeAndWait("checkBiometrics", {});
    } catch (error) {
      // This is only called if this method was triggered again without the promise being resolved
      return { status: CheckBiometricsStatus.UNKNOWN_ERROR };
    }
  }

  async setupBiometrics(secret: string): Promise<SetupBiometricsResponse> {
    if (!this.auth.user) {
      console.warn("Could not setup biometrics because user is not logged in");
      return { status: SetupBiometricsStatus.CANCELED };
    }

    try {
      const result = await this.callNativeCodeAndWait("setupBiometrics", {
        email: this.auth.user.email,
        secret: secret
      });

      // Only update the biometric status after the user completes the biometric setup in the app
      this.setIsUsingBiometrics(result.status === SetupBiometricsStatus.SUCCESS);

      return result;
    } catch (error) {
      // This is only called if this method was triggered again without the promise being resolved
      return { status: SetupBiometricsStatus.CANCELED };
    }
  }

  async decryptBiometricSecret(): Promise<DecryptBiometricSecretResponse> {
    try {
      return await this.callNativeCodeAndWait("decryptBiometricSecret", {});
    } catch (error) {
      // This is only called if this method code was triggered again without the promise being resolved
      return { status: DecryptBiometricSecretStatus.CANCELED };
    }
  }

  removeBiometrics(): void {
    this.callNative("removeBiometricSecret", {});
    this.setIsUsingBiometrics(false);
  }

  // ----------------- Native to Vue.js -----------------

  setDeviceInfo(info: NativeAppInfo): void {
    // Make sure not to send sensitive info to app insights
    this.logCallFromNativeAppToAppInsights("setDeviceInfo", {
      buildNumber: info.build.number,
      buildDate: info.build.date,
      deviceMake: info.device.make,
      deviceModel: info.device.model,
      hasBiometricsHardware: info.hasBiometricsHardware,
      hasPushToken: !!info.device.pushToken
    });

    this.deviceMake = info.device.make;
    this.deviceModel = info.device.model;
    this.buildInfo = this.createBuildInfo(info.build.number, info.build.date);
    this.nativePages = info.nativePages;
    this.hasBiometricsHardware = !!info.hasBiometricsHardware;

    if (info.device.pushToken) this.setDeviceToken(info.device.pushToken);

    // gtm.captureAppVersion(this.buildInfo);
  }

  setDeviceToken(token: string): void {
    // The website doesn't support device tokens, so if this somehow gets called for the web, don't bother trying to
    // send the token to the backend
    if (!this.isMobile) return;

    this.logCallFromNativeAppToAppInsights("setDeviceToken");

    this.auth.setPushNotificationInfo({
      provider: this.pushNotificationProvider,
      deviceToken: token
    });
  }

  navigateToPatient(patientId: string): void {
    this.logCallFromNativeAppToAppInsights("navigateToPatient", {
      patientId
    });

    this.router.push(`/patients/${patientId}/messages`);
  }

  navigateToError(): void {
    this.logCallFromNativeAppToAppInsights("navigateToError");

    this.router.push("/error");
  }

  sendGetTrustedDeviceSecretResponse(response: GetTrustedDeviceSecretResponse) {
    this.logCallFromNativeAppToAppInsights("sendGetTrustedDeviceSecretResponse", {
      hasSecret: !!response.secret
    });

    this.completeNativeCodeCallback("getTrustedDeviceSecret", response);
  }

  sendCheckBiometricsResponse(response: CheckBiometricsResponse) {
    this.logCallFromNativeAppToAppInsights("sendCheckBiometricsResponse", {
      status: response.status
    });

    this.completeNativeCodeCallback("checkBiometrics", response);
  }

  sendSetupBiometricsResponse(response: SetupBiometricsResponse) {
    this.logCallFromNativeAppToAppInsights("sendSetupBiometricsResponse", {
      status: response.status
    });

    this.completeNativeCodeCallback("setupBiometrics", response);
  }

  sendDecryptBiometricSecretResponse(response: DecryptBiometricSecretResponse) {
    this.logCallFromNativeAppToAppInsights("sendDecryptBiometricSecretResponse", {
      status: response.status
    });

    if (response.status === DecryptBiometricSecretStatus.BIOMETRICS_CHANGED) {
      this.removeBiometrics();
    }

    this.completeNativeCodeCallback("decryptBiometricSecret", response);
  }

  appResumed(immediateRefresh: boolean): void {
    this.logCallFromNativeAppToAppInsights("appResumed");

    StartRefreshInterval(immediateRefresh);
  }

  appPaused(): void {
    this.logCallFromNativeAppToAppInsights("appPaused");

    StopRefreshInterval();
  }

  // ----------------- Utility Methods -----------------

  /**
   * Helper method for calling native code where a callback is required. Unfortunately, the interface from the web to
   * native does not allow for values to be returned and sometimes the values may need to be returned at a much later
   * time (e.g. after the user performs some operation).
   *
   * To address this, calling this code registers a promise using the name of the method being called. When the native
   * app has a response, it should call a similar method named: send<Method>Response (e.g. sendSetupBiometricsResponse)
   * which will trigger the callback to be called and the promise resolved.
   *
   * If the same method is called again without a response from the native app, the promise is cancelled.
   *
   * @param method the name of the method to call on the native app
   * @param param the data to send to the native app's method
   * @returns a promise which will be resolved when the native app responds or will be rejected if the same method is
   * called again without a response from the native app
   */
  private callNativeCodeAndWait<TMethod extends keyof WebToNativeCallbacks>(
    method: TMethod,
    param: WebToNativeMethodParameter<TMethod>
  ): Promise<WebToNativeCallbacks[TMethod]> {
    return new Promise<WebToNativeCallbacks[TMethod]>((resolve, reject) => {
      // If there is a pending promise (not likely), cancel it since a new scan request has started
      this.nativePromises[method]?.reject();

      // Register the promise before calling the native app. Both Resolve and Reject will remove the pending promise
      this.nativePromises[method] = {
        resolve: result => {
          this.nativePromises[method] = undefined;
          resolve(result as WebToNativeCallbacks[TMethod]);
        },
        reject: () => {
          this.nativePromises[method] = undefined;
          reject();
        }
      };

      this.callNative(method, param);
    });
  }

  /**
   * This is the counterpart to callNativeCodeAndWait() which should be called when the native app responds, in order
   * to complete the pending promise
   * @param method the name of the method that was called on the native app and is being responded to
   * @param result the result from the call to the native app's method
   */
  private completeNativeCodeCallback<TMethod extends keyof WebToNativeCallbacks>(
    method: TMethod,
    result: WebToNativeCallbacks[TMethod]
  ) {
    this.nativePromises[method]?.resolve(result);
  }

  private logCallFromNativeAppToAppInsights(method: keyof NativeToWebMethods, properties?: Record<string, any>) {
    if (!properties) properties = {};
    properties.type = this.deviceOS;

    // appInsights.trackEvent({
    //   name: "NativeApp -> Web: " + method,
    //   properties: properties
    // });
  }
}
